package com.siriusxm.pia.views.unifiedaggregator.dataflow

import androidx.compose.runtime.*
import com.siriusxm.cmc.utils.encodeURIComponent
import com.siriusxm.pia.components.SVGIcons
import com.siriusxm.pia.components.ServiceViewContext
import com.siriusxm.pia.components.serviceContentView
import com.siriusxm.pia.components.svgIcon
import com.siriusxm.pia.views.unifiedaggregator.AggregatorService
import com.siriusxm.pia.views.unifiedaggregator.ProducerDetail
import unifiedcontent.aggregator.ConsumerDetails
import unifiedcontent.aggregator.EntityTypeConfiguration
import unifiedcontent.aggregator.PartialSchema
import unifiedcontent.aggregator.ProducerDetails
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * Sealed class representing the current selection state in the data flow visualization.
 * Determines which items are highlighted and which connections are drawn.
 */
sealed class DataFlowSelection {
    /** No item is currently selected - show all connections dimmed. */
    object None : DataFlowSelection()

    /** A producer is selected - highlight its types and their consumers. */
    data class Producer(val id: String) : DataFlowSelection()

    /** An entity type is selected - highlight its producers and consumers. */
    data class EntityType(val type: String) : DataFlowSelection()

    /** A consumer is selected - highlight its types and their producers. */
    data class Consumer(val id: String) : DataFlowSelection()
}

/**
 * Main composable for the Data Flow visualization.
 * Displays a three-column layout showing the flow of data from producers through types to consumers.
 * Interactive selection allows users to explore relationships by clicking on any node.
 *
 * @param aggregator The AggregatorService providing access to producers, types, and consumers
 */
@Composable
fun ServiceViewContext.dataFlowVisualization(aggregator: AggregatorService) {
    Style(DataFlowStyles)

    val producers = aggregator.producers
    val entityTypes = aggregator.entityTypes
    val consumers = aggregator.consumers
    val partials = aggregator.partials

    var selection by remember { mutableStateOf<DataFlowSelection>(DataFlowSelection.None) }
    var nodePositions by remember { mutableStateOf<NodePositions?>(null) }

    // Compute highlighted items based on selection
    val highlightedItems = remember(selection, producers, entityTypes, consumers, partials) {
        computeHighlightedItems(selection, producers, entityTypes, consumers, partials)
    }
    val highlightedProducers = highlightedItems.producers
    val highlightedTypes = highlightedItems.types
    val highlightedConsumers = highlightedItems.consumers
    val partialProducers = highlightedItems.partialProducers
    val partialsByProducer = highlightedItems.partialsByProducer
    val partialTypes = highlightedItems.partialTypes
    val partialsByType = highlightedItems.partialsByType

    // Compute connections to draw
    val (producerToTypeConnections, typeToConsumerConnections) = remember(selection, highlightedProducers, highlightedTypes, highlightedConsumers, producers, consumers) {
        computeConnections(selection, highlightedProducers, highlightedTypes, highlightedConsumers, producers, consumers)
    }

    // Recompute positions and scroll to highlighted items when selection changes
    LaunchedEffect(selection) {
        window.setTimeout({
            nodePositions = computeNodePositions(
                "data-flow-container",
                producers.map { it.id },
                entityTypes.map { it.type },
                consumers.map { it.id }
            )
            // Scroll columns to show highlighted items
            scrollToHighlightedItems(
                selection,
                highlightedProducers + partialProducers,
                highlightedTypes + partialTypes,
                highlightedConsumers
            )
        }, 50)
    }

    // Add resize and scroll listeners to recompute positions
    DisposableEffect(Unit) {
        val recomputePositions: (dynamic) -> Unit = {
            nodePositions = computeNodePositions(
                "data-flow-container",
                producers.map { it.id },
                entityTypes.map { it.type },
                consumers.map { it.id }
            )
        }

        // Listen for window resize
        window.addEventListener("resize", recomputePositions)

        // Add scroll listeners after a short delay to ensure DOM is ready
        var producersContent: org.w3c.dom.Element? = null
        var typesContent: org.w3c.dom.Element? = null
        var consumersContent: org.w3c.dom.Element? = null

        window.setTimeout({
            producersContent = document.getElementById("producers-content")
            typesContent = document.getElementById("types-content")
            consumersContent = document.getElementById("consumers-content")

            producersContent?.addEventListener("scroll", recomputePositions)
            typesContent?.addEventListener("scroll", recomputePositions)
            consumersContent?.addEventListener("scroll", recomputePositions)
        }, 100)

        onDispose {
            window.removeEventListener("resize", recomputePositions)
            producersContent?.removeEventListener("scroll", recomputePositions)
            typesContent?.removeEventListener("scroll", recomputePositions)
            consumersContent?.removeEventListener("scroll", recomputePositions)
        }
    }

    serviceContentView({
        breadcrumbs {
            crumb("Aggregator", "aggregator") {
                crumb("Types", null)
            }
        }
    }) {
        // Clear selection when clicking on empty space
        Div({
            id("data-flow-container")
            classes(DataFlowStyles.container)
            onClick { event ->
                // Only clear if clicking on the container itself, not a child
                if (event.target == event.currentTarget) {
                    selection = DataFlowSelection.None
                }
            }
        }) {
            // Producers column (left)
            ProducerColumn(
                producers = producers,
                selection = selection,
                highlightedProducers = highlightedProducers,
                partialProducers = partialProducers,
                partialsByProducer = partialsByProducer,
                onSelect = { producer ->
                    selection = if (selection is DataFlowSelection.Producer && (selection as DataFlowSelection.Producer).id == producer.id) {
                        DataFlowSelection.None
                    } else {
                        DataFlowSelection.Producer(producer.id)
                    }
                },
                onNavigate = { path -> aggregator.navigate(path) }
            )

            // Types column (center)
            TypeColumn(
                types = entityTypes,
                selection = selection,
                highlightedTypes = highlightedTypes,
                partialTypes = partialTypes,
                partialsByType = partialsByType,
                onSelect = { type ->
                    selection = if (selection is DataFlowSelection.EntityType && (selection as DataFlowSelection.EntityType).type == type.type) {
                        DataFlowSelection.None
                    } else {
                        DataFlowSelection.EntityType(type.type)
                    }
                },
                onNavigate = { path -> aggregator.navigate(path) }
            )

            // Consumers column (right)
            ConsumerColumn(
                consumers = consumers,
                selection = selection,
                highlightedConsumers = highlightedConsumers,
                onSelect = { consumer ->
                    selection = if (selection is DataFlowSelection.Consumer && (selection as DataFlowSelection.Consumer).id == consumer.id) {
                        DataFlowSelection.None
                    } else {
                        DataFlowSelection.Consumer(consumer.id)
                    }
                },
                onNavigate = { path -> aggregator.navigate(path) }
            )

            // SVG overlay for connection lines
            ConnectionOverlay(
                containerId = "data-flow-container",
                producerToTypeConnections = producerToTypeConnections,
                typeToConsumerConnections = typeToConsumerConnections,
                positions = nodePositions
            )
        }
    }
}

/**
 * Data class containing the computed highlighted items for the visualization.
 */
private data class HighlightedItems(
    val producers: Set<String>,
    val types: Set<String>,
    val consumers: Set<String>,
    val partialProducers: Set<String>,
    /** Maps producer ID to the list of partial names that connect them to the selected type(s) */
    val partialsByProducer: Map<String, List<String>>,
    /** Types that are affected by partials from the selected producer */
    val partialTypes: Set<String> = emptySet(),
    /** Maps type to the list of partial names that a selected producer can publish to it */
    val partialsByType: Map<String, List<String>> = emptyMap()
)

/**
 * Extracts the entity type identifier from a partial schema type URL.
 * Partial schema types are formatted as URLs like ".../entityType:version"
 */
private fun extractTypeFromPartialSchemaType(schemaType: String): String? {
    return schemaType.substringAfterLast("/", "")
        .substringBefore(":").ifBlank { null }
}

/**
 * Result of finding partial producers for a type.
 */
private data class PartialProducerResult(
    val producerIds: Set<String>,
    val partialsByProducer: Map<String, List<String>>
)

/**
 * Checks whether a producer has base type access for a given entity type.
 * Uses typePermissions if available, otherwise falls back to allowedTypes.
 */
private fun producerHasBaseType(producer: ProducerDetails, typeId: String): Boolean {
    val typePerms = producer.typePermissions
    return if (!typePerms.isNullOrEmpty()) {
        typePerms.any { it.type == typeId && it.base == true }
    } else {
        producer.allowedTypes?.contains(typeId) == true
    }
}

/**
 * Returns the set of base type IDs for a producer.
 * Uses typePermissions if available, otherwise falls back to allowedTypes.
 */
private fun producerBaseTypes(producer: ProducerDetails): Set<String> {
    val typePerms = producer.typePermissions
    return if (!typePerms.isNullOrEmpty()) {
        typePerms.filter { it.base == true }.map { it.type }.toSet()
    } else {
        producer.allowedTypes?.toSet() ?: emptySet()
    }
}

/**
 * Checks whether a producer is allowed to publish a specific partial for a specific type.
 * If the producer has typePermissions configured, the partial must be listed under the
 * matching type entry. Otherwise, falls back to the legacy allowedPartials field. A partial in
 * unrestrictedPartials is allowed for every type (UCA-1162).
 */
private fun producerCanPublishPartialForType(
    producer: ProducerDetails,
    partialId: String,
    typeId: String
): Boolean {
    if (producer.unrestrictedPartials.orEmpty().contains(partialId)) return true
    val typePerms = producer.typePermissions
    return if (!typePerms.isNullOrEmpty()) {
        // New model: check if the partial is listed under this type's permissions
        typePerms.any { it.type == typeId && it.partials?.contains(partialId) == true }
    } else {
        // Legacy model: allowedPartials grants access to all types the partial supports
        producer.allowedPartials?.contains(partialId) == true
    }
}

/**
 * Finds producers that can publish partials for a given entity type.
 * Respects typePermissions when configured, limiting partial connections to
 * only those types explicitly enabled for the producer.
 *
 * @param type The entity type identifier
 * @param partials All partial schemas in the system
 * @param producers All producers in the system
 * @return PartialProducerResult containing producer IDs and the partials that connect them
 */
private fun findPartialProducersForType(
    type: String,
    partials: List<PartialSchema>,
    producers: List<ProducerDetails>
): PartialProducerResult {
    // Find partials that apply to this type (per their schema)
    val relevantPartials = partials.filter { partial ->
        partial.types?.any { extractTypeFromPartialSchemaType(it) == type || extractTypeFromPartialSchemaType(it) == "*" } == true
    }

    // Find producers that can publish these partials for this specific type
    val partialsByProducer = mutableMapOf<String, MutableList<String>>()

    producers.forEach { producer ->
        val allowedPartials = relevantPartials.filter { partial ->
            producerCanPublishPartialForType(producer, partial.id, type)
        }
        if (allowedPartials.isNotEmpty()) {
            val partialNames = allowedPartials.map { it.name ?: it.id }
            partialsByProducer[producer.id] = partialNames.toMutableList()
        }
    }

    return PartialProducerResult(
        producerIds = partialsByProducer.keys,
        partialsByProducer = partialsByProducer
    )
}

/**
 * Computes which items should be highlighted based on the current selection.
 *
 * @param selection The current selection state
 * @param producers All producers in the system
 * @param entityTypes All entity types in the system
 * @param consumers All consumers in the system
 * @param partials All partial schemas in the system
 * @return HighlightedItems containing producer IDs, type names, consumer IDs, and partial producer IDs
 */
@Suppress("UNUSED_PARAMETER")
private fun computeHighlightedItems(
    selection: DataFlowSelection,
    producers: List<ProducerDetails>,
    entityTypes: List<EntityTypeConfiguration>,
    consumers: List<ConsumerDetails>,
    partials: List<PartialSchema>
): HighlightedItems {
    return when (selection) {
        is DataFlowSelection.None -> HighlightedItems(
            producers = emptySet(),
            types = emptySet(),
            consumers = emptySet(),
            partialProducers = emptySet(),
            partialsByProducer = emptyMap()
        )

        is DataFlowSelection.Producer -> {
            val producer = producers.find { it.id == selection.id }

            // Determine base types from typePermissions if available, otherwise fall back to allowedTypes
            val types = if (!producer?.typePermissions.isNullOrEmpty()) {
                producer!!.typePermissions!!.filter { it.base == true }.map { it.type }.toSet()
            } else {
                producer?.allowedTypes?.toSet() ?: emptySet()
            }

            val relatedConsumers = consumers.filter { consumer ->
                consumer.types?.any { it in types } == true
            }.map { it.id }.toSet()

            // Find types that this producer can affect via partials
            val partialsByType = mutableMapOf<String, MutableList<String>>()
            if (producer != null) {
                if (!producer.typePermissions.isNullOrEmpty()) {
                    // New model: iterate type permissions entries and collect partials per type
                    producer.typePermissions!!.forEach { perm ->
                        perm.partials?.forEach { partialId ->
                            if (perm.type !in types || perm.base != true) {
                                // Only show as partial connection if not already a base type connection
                                val partial = partials.find { it.id == partialId }
                                if (partial != null) {
                                    partialsByType.getOrPut(perm.type) { mutableListOf() }
                                        .add(partial.name ?: partial.id)
                                }
                            }
                        }
                    }
                } else {
                    // Legacy model: use allowedPartials and partial schema types
                    producer.allowedPartials?.forEach { partialId ->
                        val partial = partials.find { it.id == partialId }
                        partial?.types?.forEach { schemaType ->
                            val typeId = extractTypeFromPartialSchemaType(schemaType)
                            if (typeId != null && typeId !in types) {
                                partialsByType.getOrPut(typeId) { mutableListOf() }
                                    .add(partial.name ?: partial.id)
                            }
                        }
                    }
                }
            }

            HighlightedItems(
                producers = setOf(selection.id),
                types = types,
                consumers = relatedConsumers,
                partialProducers = emptySet(),
                partialsByProducer = emptyMap(),
                partialTypes = partialsByType.keys,
                partialsByType = partialsByType.mapValues { it.value.distinct() }
            )
        }

        is DataFlowSelection.EntityType -> {
            val type = selection.type
            val relatedProducers = producers.filter { producer ->
                producerHasBaseType(producer, type)
            }.map { it.id }.toSet()
            val relatedConsumers = consumers.filter { consumer ->
                consumer.types?.contains(type) == true
            }.map { it.id }.toSet()
            // Find producers that can publish partials for this type
            val partialResult = findPartialProducersForType(type, partials, producers)
            // Exclude producers already in the direct list
            val partialOnlyProducers = partialResult.producerIds - relatedProducers
            val partialOnlyMap = partialResult.partialsByProducer.filterKeys { it in partialOnlyProducers }
            HighlightedItems(
                producers = relatedProducers,
                types = setOf(type),
                consumers = relatedConsumers,
                partialProducers = partialOnlyProducers,
                partialsByProducer = partialOnlyMap
            )
        }

        is DataFlowSelection.Consumer -> {
            val consumer = consumers.find { it.id == selection.id }
            val types = consumer?.types?.toSet() ?: emptySet()
            val relatedProducers = producers.filter { producer ->
                types.any { producerHasBaseType(producer, it) }
            }.map { it.id }.toSet()
            // Find producers that can publish partials for any of the consumed types
            val combinedPartialsByProducer = mutableMapOf<String, MutableList<String>>()
            types.forEach { type ->
                val partialResult = findPartialProducersForType(type, partials, producers)
                partialResult.partialsByProducer.forEach { (producerId, partialNames) ->
                    combinedPartialsByProducer.getOrPut(producerId) { mutableListOf() }.addAll(partialNames)
                }
            }
            // Exclude producers already in the direct list
            val partialOnlyProducers = combinedPartialsByProducer.keys - relatedProducers
            val partialOnlyMap = combinedPartialsByProducer.filterKeys { it in partialOnlyProducers }
                .mapValues { it.value.distinct() }
            HighlightedItems(
                producers = relatedProducers,
                types = types,
                consumers = setOf(selection.id),
                partialProducers = partialOnlyProducers,
                partialsByProducer = partialOnlyMap
            )
        }
    }
}

/**
 * Computes the connections to draw between nodes.
 *
 * @return Pair of (producer-to-type connections, type-to-consumer connections)
 */
private fun computeConnections(
    selection: DataFlowSelection,
    highlightedProducers: Set<String>,
    highlightedTypes: Set<String>,
    highlightedConsumers: Set<String>,
    producers: List<ProducerDetails>,
    consumers: List<ConsumerDetails>
): Pair<List<Connection>, List<Connection>> {
    val producerToType = mutableListOf<Connection>()
    val typeToConsumer = mutableListOf<Connection>()

    // Build producer to type connections
    producers.forEach { producer ->
        producerBaseTypes(producer).forEach { type ->
            val highlighted = producer.id in highlightedProducers && type in highlightedTypes
            producerToType.add(Connection(producer.id, type, highlighted))
        }
    }

    // Build type to consumer connections
    consumers.forEach { consumer ->
        consumer.types?.forEach { type ->
            val highlighted = type in highlightedTypes && consumer.id in highlightedConsumers
            typeToConsumer.add(Connection(type, consumer.id, highlighted))
        }
    }

    return Pair(producerToType, typeToConsumer)
}

/**
 * Scrolls each column to show as many highlighted items as possible.
 * When a selection is made, the other columns scroll to bring their highlighted items into view.
 *
 * @param selection The current selection state
 * @param highlightedProducers Set of highlighted producer IDs (including partial producers)
 * @param highlightedTypes Set of highlighted type identifiers
 * @param highlightedConsumers Set of highlighted consumer IDs
 */
private fun scrollToHighlightedItems(
    selection: DataFlowSelection,
    highlightedProducers: Set<String>,
    highlightedTypes: Set<String>,
    highlightedConsumers: Set<String>
) {
    // Don't scroll if nothing is selected
    if (selection is DataFlowSelection.None) return

    // Scroll producers column (when type or consumer is selected)
    if (selection !is DataFlowSelection.Producer && highlightedProducers.isNotEmpty()) {
        scrollColumnToShowItems("producers-content", highlightedProducers.map { "producer-$it" })
    }

    // Scroll types column (when producer or consumer is selected)
    if (selection !is DataFlowSelection.EntityType && highlightedTypes.isNotEmpty()) {
        scrollColumnToShowItems("types-content", highlightedTypes.map { "type-$it" })
    }

    // Scroll consumers column (when producer or type is selected)
    if (selection !is DataFlowSelection.Consumer && highlightedConsumers.isNotEmpty()) {
        scrollColumnToShowItems("consumers-content", highlightedConsumers.map { "consumer-$it" })
    }
}

/**
 * Scrolls a column container to show as many of the specified items as possible.
 * Calculates the optimal scroll position to maximize visible highlighted items.
 *
 * @param containerId The ID of the scrollable container
 * @param itemIds List of DOM IDs for the items to show
 */
private fun scrollColumnToShowItems(containerId: String, itemIds: List<String>) {
    val container = document.getElementById(containerId) ?: return

    // Find all the highlighted elements and their positions
    val elements = itemIds.mapNotNull { document.getElementById(it) }
    if (elements.isEmpty()) return

    val containerRect = container.getBoundingClientRect()
    val containerScrollTop = container.scrollTop

    // Find the bounding box of all highlighted items relative to the container
    var minTop = Double.MAX_VALUE
    var maxBottom = Double.MIN_VALUE

    elements.forEach { element ->
        val rect = element.getBoundingClientRect()
        val relativeTop = rect.top - containerRect.top + containerScrollTop
        val relativeBottom = relativeTop + rect.height

        if (relativeTop < minTop) minTop = relativeTop
        if (relativeBottom > maxBottom) maxBottom = relativeBottom
    }

    val highlightedHeight = maxBottom - minTop
    val containerHeight = containerRect.height

    // Calculate optimal scroll position
    val targetScroll = if (highlightedHeight <= containerHeight) {
        // All items can fit - center them
        (minTop - (containerHeight - highlightedHeight) / 2).coerceAtLeast(0.0)
    } else {
        // Items don't all fit - scroll to show the first one at the top
        minTop.coerceAtLeast(0.0)
    }

    // Apply smooth scroll
    container.scrollTop = targetScroll
}

/**
 * Column displaying all producers.
 * Producers are shown as clickable nodes that highlight their data flow when selected.
 * Partial producers (those that can publish partials to the selected type) are shown in a different color.
 *
 * @param producers List of all producers to display
 * @param selection The current selection state
 * @param highlightedProducers Set of producer IDs that are directly related to the selection
 * @param partialProducers Set of producer IDs that can publish partials to the selected type
 * @param partialsByProducer Map from producer ID to the list of partial names they can publish
 * @param onSelect Callback when a producer is clicked
 * @param onNavigate Callback to navigate to a detail page
 */
@Composable
private fun ProducerColumn(
    producers: List<ProducerDetails>,
    selection: DataFlowSelection,
    highlightedProducers: Set<String>,
    partialProducers: Set<String>,
    partialsByProducer: Map<String, List<String>>,
    onSelect: (ProducerDetails) -> Unit,
    onNavigate: (String) -> Unit
) {
    Div({ classes(DataFlowStyles.column) }) {
        Div({ classes(DataFlowStyles.columnHeader) }) {
            Text("Producers")
        }

        Div({ classes(DataFlowStyles.columnContentWrapper) }) {
            Div({ classes(DataFlowStyles.columnGradientTop) })
            Div({
                id("producers-content")
                classes(DataFlowStyles.columnContent)
            }) {
                if (producers.isEmpty()) {
                    Div({ classes(DataFlowStyles.emptyState) }) {
                        Text("No producers configured")
                    }
                } else {
                    producers.sortedBy { it.name?.lowercase() ?: it.id.lowercase() }.forEach { producer ->
                        val isSelected = selection is DataFlowSelection.Producer && selection.id == producer.id
                        val isHighlighted = producer.id in highlightedProducers
                        val isPartialHighlighted = producer.id in partialProducers
                        val isDimmed = selection !is DataFlowSelection.None && !isHighlighted && !isPartialHighlighted
                        val relatedPartials = partialsByProducer[producer.id] ?: emptyList()

                        NodeItem(
                            id = "producer-${producer.id}",
                            name = producer.name ?: producer.id,
                            secondaryText = if (producer.name != null) producer.id else null,
                            detailPath = ProducerDetail(producer.id).location().toPath(),
                            isSelected = isSelected,
                            isHighlighted = isHighlighted && !isSelected,
                            isPartialHighlighted = isPartialHighlighted,
                            relatedPartials = relatedPartials,
                            isDimmed = isDimmed,
                            isDisabled = producer.disabled == true,
                            onClick = { onSelect(producer) },
                            onNavigate = onNavigate
                        )
                    }
                }
            }
            Div({ classes(DataFlowStyles.columnGradientBottom) })
        }
    }
}

/**
 * Column displaying all entity types.
 * Types are shown as clickable nodes in the center column, connecting producers to consumers.
 * Types that can only receive partials from the selected producer are shown in pink.
 *
 * @param types List of all entity types to display
 * @param selection The current selection state
 * @param highlightedTypes Set of type identifiers that are directly related to the selection
 * @param partialTypes Set of type identifiers that can receive partials from the selected producer
 * @param partialsByType Map from type identifier to the list of partial names that can be published to it
 * @param onSelect Callback when a type is clicked
 * @param onNavigate Callback to navigate to a detail page
 */
@Composable
private fun TypeColumn(
    types: List<EntityTypeConfiguration>,
    selection: DataFlowSelection,
    highlightedTypes: Set<String>,
    partialTypes: Set<String>,
    partialsByType: Map<String, List<String>>,
    onSelect: (EntityTypeConfiguration) -> Unit,
    onNavigate: (String) -> Unit
) {
    Div({
        classes(DataFlowStyles.column, DataFlowStyles.typesColumn)
    }) {
        Div({ classes(DataFlowStyles.columnHeader) }) {
            Text("Types")
        }

        Div({ classes(DataFlowStyles.columnContentWrapper) }) {
            Div({ classes(DataFlowStyles.columnGradientTop) })
            Div({
                id("types-content")
                classes(DataFlowStyles.columnContent)
            }) {
                if (types.isEmpty()) {
                    Div({ classes(DataFlowStyles.emptyState) }) {
                        Text("No entity types configured")
                    }
                } else {
                    types.sortedBy { it.name.lowercase() }.forEach { type ->
                        val isSelected = selection is DataFlowSelection.EntityType && selection.type == type.type
                        val isHighlighted = type.type in highlightedTypes
                        val isPartialHighlighted = type.type in partialTypes
                        val isDimmed = selection !is DataFlowSelection.None && !isHighlighted && !isPartialHighlighted
                        val relatedPartials = partialsByType[type.type] ?: emptyList()

                        NodeItem(
                            id = "type-${type.type}",
                            name = type.name,
                            secondaryText = type.type,
                            detailPath = "types/entities/${type.type}",
                            isSelected = isSelected,
                            isHighlighted = isHighlighted && !isSelected,
                            isPartialHighlighted = isPartialHighlighted,
                            relatedPartials = relatedPartials,
                            isDimmed = isDimmed,
                            isDisabled = false,
                            onClick = { onSelect(type) },
                            onNavigate = onNavigate
                        )
                    }
                }
            }
            Div({ classes(DataFlowStyles.columnGradientBottom) })
        }
    }
}

/**
 * Column displaying all consumers.
 * Consumers are shown as clickable nodes that highlight their data sources when selected.
 */
@Composable
private fun ConsumerColumn(
    consumers: List<ConsumerDetails>,
    selection: DataFlowSelection,
    highlightedConsumers: Set<String>,
    onSelect: (ConsumerDetails) -> Unit,
    onNavigate: (String) -> Unit
) {
    Div({ classes(DataFlowStyles.column) }) {
        Div({ classes(DataFlowStyles.columnHeader) }) {
            Text("Consumers")
        }

        Div({ classes(DataFlowStyles.columnContentWrapper) }) {
            Div({ classes(DataFlowStyles.columnGradientTop) })
            Div({
                id("consumers-content")
                classes(DataFlowStyles.columnContent)
            }) {
                if (consumers.isEmpty()) {
                    Div({ classes(DataFlowStyles.emptyState) }) {
                        Text("No consumers configured")
                    }
                } else {
                    consumers.sortedBy { it.name?.lowercase() ?: it.id.lowercase() }.forEach { consumer ->
                        val isSelected = selection is DataFlowSelection.Consumer && selection.id == consumer.id
                        val isHighlighted = consumer.id in highlightedConsumers
                        val isDimmed = selection !is DataFlowSelection.None && !isHighlighted

                        NodeItem(
                            id = "consumer-${consumer.id}",
                            name = consumer.name ?: consumer.id,
                            secondaryText = if (consumer.name != null) consumer.id else null,
                            detailPath = "consumers/${encodeURIComponent(consumer.id)}",
                            isSelected = isSelected,
                            isHighlighted = isHighlighted && !isSelected,
                            isPartialHighlighted = false,
                            isDimmed = isDimmed,
                            isDisabled = consumer.disabled == true,
                            onClick = { onSelect(consumer) },
                            onNavigate = onNavigate
                        )
                    }
                }
            }
            Div({ classes(DataFlowStyles.columnGradientBottom) })
        }
    }
}

/**
 * A single node item in any of the three columns.
 * Displays the node name, with secondary text and info icon only shown when selected for a compact view.
 *
 * @param id The DOM ID for this node (used for position calculations)
 * @param name The primary display name
 * @param secondaryText Optional secondary text (e.g., ID when name differs) - only shown when selected
 * @param detailPath The navigation path to the detail page for this item
 * @param isSelected Whether this node is the currently selected item
 * @param isHighlighted Whether this node is related to the selected item (direct type relationship)
 * @param isPartialHighlighted Whether this node can publish partials to the selected type
 * @param relatedPartials List of partial names this producer can publish to the selected type
 * @param isDimmed Whether this node should be dimmed (unrelated to selection)
 * @param isDisabled Whether this producer/consumer is disabled
 * @param onClick Callback when the node is clicked
 * @param onNavigate Callback to navigate to the detail page
 */
@Composable
private fun NodeItem(
    id: String,
    name: String,
    secondaryText: String?,
    detailPath: String,
    isSelected: Boolean,
    isHighlighted: Boolean,
    isPartialHighlighted: Boolean,
    relatedPartials: List<String> = emptyList(),
    isDimmed: Boolean,
    isDisabled: Boolean,
    onClick: () -> Unit,
    onNavigate: (String) -> Unit
) {
    Div({
        id(id)
        classes(DataFlowStyles.node)
        if (isSelected) classes(DataFlowStyles.nodeSelected)
        if (isHighlighted) classes(DataFlowStyles.nodeHighlighted)
        if (isPartialHighlighted) classes(DataFlowStyles.nodePartialHighlighted)
        if (isDimmed) classes(DataFlowStyles.nodeDimmed)
        onClick { event ->
            event.stopPropagation()
            onClick()
        }
    }) {
        Div({
            classes(DataFlowStyles.nodeName)
            style {
                display(DisplayStyle.Flex)
                property("align-items", "center")
                property("justify-content", "space-between")
                gap(0.5.em)
            }
        }) {
            Span {
                Text(name)
                if (isDisabled) {
                    Span({ classes(DataFlowStyles.disabledBadge) }) {
                        Text("DISABLED")
                    }
                }
            }
            // Show info icon when selected to navigate to detail page
            if (isSelected) {
                svgIcon(SVGIcons.infoCircle) {
                    size = 18.px
                    title = "View details"
                    action {
                        onNavigate(detailPath)
                    }
                }
            }
        }
        // Only show secondary text (ID) when the item is selected
        if (isSelected && secondaryText != null && secondaryText != name) {
            Div({ classes(DataFlowStyles.nodeId) }) {
                Text(secondaryText)
            }
        }
        // Show related partials when this producer is partial-highlighted
        if (isPartialHighlighted && relatedPartials.isNotEmpty()) {
            Div({ classes(DataFlowStyles.nodeId) }) {
                Text("Partials: ${relatedPartials.joinToString(", ")}")
            }
        }
    }
}
